// src/components/widgets/avatar-uploader.js
import { useRef, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { Avatar, Box, SvgIcon } from '@mui/material';
import { alpha } from '@mui/system/colorManipulator';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { fileToBase64, base64ToBlob } from 'src/utils/file-to-base64';
import { getAvatar } from 'src/services/AssetApi';

const AvatarUploader = forwardRef(({
  avatar,
  gender,
  size = 172,
  sx = {},
  onBlobChange,
}, ref) => {
  const avatarSrc = useMemo(() => getAvatar(avatar, gender), [avatar, gender]);
  const fileInputRef = useRef();

  const [coverBase, setCoverBase] = useState();

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const base64 = await fileToBase64(file); // Includes "data:image/png;base64,..."
    setCoverBase(base64);

    const blob = base64ToBlob(base64, file.type || 'image/png');

    if (onBlobChange) {
      onBlobChange(blob, base64);
    }
  };

  useImperativeHandle(ref, () => ({
    reset: () => {
      fileInputRef.current.value = '';
      setCoverBase(undefined);
    },
  }));

  return (
    <Box
      sx={{
        position: 'relative',
        cursor: 'pointer',
        width: size,
        height: size,
        ...sx,
      }}
      onClick={handleAvatarClick}
    >
      <Avatar
        src={coverBase || avatarSrc || undefined}
        sx={{
          height: size,
          width: size,
          fontSize: size * 0.5,
        }}
      >
        {!(coverBase || avatarSrc) && (
          <SvgIcon sx={{ fontSize: size * 0.6, color: 'grey.400' }}>
            <PersonOutlineIcon />
          </SvgIcon>
        )}
      </Avatar>

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: size,
          height: size,
          borderRadius: '50%',
          overflow: 'hidden',
          backgroundColor: (theme) => alpha(theme.palette.neutral[700], 0.5),
          color: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: 0,
          transition: 'opacity 0.2s',
          '&:hover': {
            opacity: 1,
          },
        }}
      >
        <PhotoCameraOutlinedIcon sx={{ fontSize: size * 0.3 }} />
      </Box>


      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
});

export default AvatarUploader;
